import { Box, CircularProgress, Paper, useTheme } from '@mui/material';
import { useMemo, useState } from 'react';
import { Page } from 'react-pdf';

import { NavigationControls } from '~/components/documentPreview/NavigationControls';
import { UploadDocumentType } from '~/components/uploadDocument/AddManuallyDropdown';
import type { LiftAIDocumentUntagged } from '~/components/uploadDocument/UploadDocumentDialogFlow';
import { useResizeObserver } from '~/hooks/useResizeObserver';

import Outline from './Outline';
import ZoomControls from './ZoomControls';

interface DocumentPreviewProps {
  documents: (LiftAIDocumentUntagged & { documentType: UploadDocumentType })[];
  currentPage: number;
  containerHeight: number;
}

const heightRatio = 0.773; // Assuming Letter dimensions, we may want to make this based on the modal in the future
const outlineWidth = 60;

// interface ILiftAIPDFPage extends PageProps {
//   pdf: pdfjs.PDFDocumentProxy;
// }
// // Hack to get access to a missing property
// const LiftAIPDFPage = Page as unknown as React.FC<ILiftAIPDFPage>;

const DocumentPreview: React.FC<DocumentPreviewProps> = ({
  documents,
  currentPage,
  containerHeight: parentContainerHeight,
}) => {
  const theme = useTheme();
  const [scale, setScale] = useState(1.1);
  const [pdfContainerRef, { height: pdfContainerHeight }] = useResizeObserver<HTMLDivElement>();

  const paddingAdjustment = parseFloat(theme.spacing(1)) * 2;
  // Calculate the PDF width while respecting the container height so that we don't overflow
  const pdfHeight = Math.min(pdfContainerHeight, parentContainerHeight) - paddingAdjustment;
  const pdfWidth = pdfHeight * heightRatio;

  const totalPages = useMemo(() => documents.length, [documents.length]);
  const document = useMemo(() => documents[currentPage - 1], [currentPage, documents]);
  const [page, setPage] = useState(currentPage);

  // `document.file` is the loaded pdfjs PDFDocumentProxy. It can be null/undefined
  // before the document finishes loading, so guard the react-pdf <Page>/<Outline>
  // render — otherwise react-pdf calls getPage() on a null proxy and crashes the
  // page (Sentry ASSET-MANAGEMENT-VV).
  const pdf = document.fileType === 'pdf' ? document.file : null;

  return (
    <Box display="flex" height="100%">
      {document.fileType === 'pdf' ? (
        <>
          <Box flex={`0 0 ${outlineWidth + paddingAdjustment * 2}px`} px={1} overflow="scroll">
            {pdf ? <Outline pdf={pdf} currentPage={currentPage} width={outlineWidth} /> : null}
          </Box>
          <Box flex="1 1 auto" minWidth={0} px={1} ref={pdfContainerRef}>
            <Paper
              elevation={3}
              sx={{
                position: 'relative',
                flex: 1,
                margin: '0 auto',
                overflow: 'auto',
                height: pdfHeight,
                maxWidth: pdfWidth,
              }}
            >
              {document.documentType !== UploadDocumentType.initial && (
                <NavigationControls
                  type={documents[page - 1].documentType}
                  defaultPage={currentPage}
                  numberOfPages={totalPages}
                  onNavigationChange={(page) => {
                    setPage(page);
                    setScale(1.0);
                  }}
                />
              )}
              {pdf ? (
                <Page
                  pdf={pdf}
                  pageNumber={
                    document.documentType !== UploadDocumentType.initial ? page : currentPage
                  }
                  width={pdfWidth}
                  scale={scale}
                  renderAnnotationLayer={false}
                />
              ) : (
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  height="100%"
                  minHeight={120}
                >
                  <CircularProgress />
                </Box>
              )}
              <ZoomControls scale={scale} onSetZoomLevel={setScale} />
            </Paper>
          </Box>
        </>
      ) : (
        <>Not Implmented</>
      )}
    </Box>
  );
};

export default DocumentPreview;
